import { useCallback, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { Trans } from "@lingui/react/macro";
import { Tabs, TabsContent, TabsTrigger } from "@/components/ui/tabs";
import { ScrollableTabsList } from "@/components/ui/scrollable-tabs-list";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { PageContainer } from "@/components/layout/PageContainer";
import { PageHeader } from "@/components/layout/PageHeader";
import { useRegisterBreadcrumb } from "@/contexts/BreadcrumbContext";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { useCanOpen } from "@/hooks/useCanOpen";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Ban, CheckCircle, FileDown, MoreHorizontal, PenLine } from "lucide-react";
import { useLingui } from "@lingui/react";
import { useDeleteSettlingIn } from "@/hooks/useSettlingIn";
import {
  useDownloadSettlingInProgressPDF,
  useDownloadSettlingInSchedulePDF,
} from "@/hooks/useSettlingInPdf";
import { useChild } from "@/hooks/useChildren";
import { useEntityLabels } from "@/hooks/settings/useLabels";
import type { SettlingInPlanDetail } from "@/types/settlingIn";
import { EntityLabels } from "@/components/ui/entity-labels";
import { LifecycleStepper } from "@/components/settling-in/LifecycleStepper";
import { LifecycleTimeline } from "@/components/settling-in/LifecycleTimeline";
import { ClosureChecklistCard } from "@/components/settling-in/ClosureChecklistCard";
import { OverviewTab } from "./OverviewTab";
import { DaysTab } from "./DaysTab";
import { ProgressTab } from "./ProgressTab";
import { CompleteDialog } from "./CompleteDialog";
import { SignOffDialog } from "./SignOffDialog";
import { useRegisterBackNavigation } from "@/hooks/useRegisterBackNavigation";
import { useBackNavigation } from "@/hooks/useBackNavigation";
import { useEscapeCancel } from "@/hooks/useEscapeCancel";

interface SettlingInDetailProps {
  plan: SettlingInPlanDetail;
  planId: string;
}

export function SettlingInDetail({ plan, planId }: SettlingInDetailProps) {
  const { i18n } = useLingui();
  useRegisterBackNavigation("/settling-in");
  const { goBack } = useBackNavigation();
  useEscapeCancel(goBack);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  type SettlingInTab = "overview" | "days" | "progress";
  const validTabs: SettlingInTab[] = ["overview", "days", "progress"];
  const rawTabParam = searchParams.get("tab");
  // Legacy `?tab=schedule|tracking` links now land on the merged Days tab (S13).
  const tabParam: SettlingInTab | null =
    rawTabParam === "schedule" || rawTabParam === "tracking"
      ? "days"
      : (rawTabParam as SettlingInTab | null);
  const activeTab: SettlingInTab = tabParam && validTabs.includes(tabParam) ? tabParam : "overview";

  const setActiveTab = useCallback((tab: string) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (tab === "overview") next.delete("tab");
      else next.set("tab", tab);
      return next;
    }, { replace: true });
  }, [setSearchParams]);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);
  const [showSignOffDialog, setShowSignOffDialog] = useState(false);

  const deleteMutation = useDeleteSettlingIn();
  const downloadScheduleMutation = useDownloadSettlingInSchedulePDF();
  const downloadProgressMutation = useDownloadSettlingInProgressPDF();

  /**
   * `sign_off_date` is the canonical signed-off signal — it is what
   * `deriveSettlingInStage` and `ClosureChecklistCard` both read. Deriving the
   * header and the dialog from `sign_off_contact_id` instead let the two
   * disagree: a plan carrying a date but no contact read "signed off" on the
   * lozenge and the checklist while the header still offered Sign Off.
   */
  const isSignedOff = !!plan.sign_off_date;

  const { data: childData } = useChild(plan.child_id || "");
  const { data: childLabels } = useEntityLabels(plan.child_id || undefined, "children");

  const childName = [plan.child_firstname, plan.child_lastname].filter(Boolean).join(" ");
  useRegisterBreadcrumb(childName || undefined);
  useDocumentTitle(childName);
  const canOpen = useCanOpen();
  const childPath = plan.child_id ? `/children/${plan.child_id}/edit` : undefined;

  // The hook toasts a failure; the rejection keeps the confirm dialog open.
  const handleDelete = async () => {
    await deleteMutation.mutateAsync(planId);
    navigate("/settling-in");
  };

  return (
    <>
      {/* The closure rail sits next to the tabs, so the page takes the list
          width rather than the narrower detail width. */}
      <PageContainer width="list" className="flex h-full flex-col">
        <div className="flex-none pb-4">
          <PageHeader
            className="mb-0"
            title={
              childPath && canOpen(childPath) ? (
                <Link to={childPath} className="underline-offset-4 hover:underline">
                  {childName}
                </Link>
              ) : (
                childName
              )
            }
            badges={
              <>
                {childData?.childInternalId && (
                  <span className="text-sm text-muted-foreground tabular-nums">
                    {childData.childInternalId}
                  </span>
                )}
                <EntityLabels labels={childLabels} />
              </>
            }
            actions={
              <>
                {!plan.settling_in_completed && (
                  <Button
                    variant="outline"
                    onClick={() => setShowCompleteDialog(true)}
                  >
                    <CheckCircle className="h-4 w-4" aria-hidden="true" />
                    <Trans id="settlingIn.actions.complete">Complete</Trans>
                  </Button>
                )}
                {!isSignedOff && (
                  <Button
                    variant="outline"
                    onClick={() => setShowSignOffDialog(true)}
                  >
                    <PenLine className="h-4 w-4" aria-hidden="true" />
                    <Trans id="settlingIn.actions.signOff">Sign Off</Trans>
                  </Button>
                )}
                {/* The plan's one destructive affordance. It replaces both the
                    bare red header Delete and the second, differently-worded
                    Terminate that used to sit on the OverviewTab status card —
                    the more prominent of which omitted the contract reminder the
                    guide calls essential. */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      size="icon"
                      aria-label={i18n._({ id: "settlingIn.actions.menu", message: "Actions" })}
                    >
                      <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {/* The two printable documents (#387). They sit ABOVE the
                        separator so the destructive Terminate item keeps a
                        deliberate gap between it and everything harmless. */}
                    <DropdownMenuItem
                      onSelect={() => downloadScheduleMutation.mutate({ planId, lang: i18n.locale })}
                      disabled={downloadScheduleMutation.isPending}
                    >
                      <FileDown className="h-4 w-4 mr-2" aria-hidden="true" />
                      <Trans id="settlingIn.actions.downloadSchedulePdf">Download settling-in plan (PDF)</Trans>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onSelect={() => downloadProgressMutation.mutate({ planId, lang: i18n.locale })}
                      disabled={downloadProgressMutation.isPending}
                    >
                      <FileDown className="h-4 w-4 mr-2" aria-hidden="true" />
                      <Trans id="settlingIn.actions.downloadProgressPdf">Download progress summary (PDF)</Trans>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      className="text-destructive focus:text-destructive"
                      onSelect={() => setShowDeleteDialog(true)}
                      disabled={deleteMutation.isPending}
                    >
                      <Ban className="h-4 w-4 mr-2" aria-hidden="true" />
                      <Trans id="settlingIn.actions.terminate">Terminate</Trans>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            }
          />

            {/* Lifecycle at the top, where "where does this plan stand?" is asked. */}
            <LifecycleStepper plan={plan} className="mt-4" />

            {/* Who did what, when, and through which surface (ST-5/S15). */}
            <LifecycleTimeline plan={plan} className="mt-4" />
        </div>

        {/* Tabs + closure rail (D7). DOM order is tabs → card, which gives the
            narrow stack "card below the tabs" for free and the wide `lg:flex-row`
            right rail. A simple responsive stack — no collapsible banner. */}
        <div className="flex flex-1 flex-col gap-6 lg:flex-row lg:items-start">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 min-w-0">
            <ScrollableTabsList className="mb-6">
              <TabsTrigger value="overview">
                <Trans id="settlingIn.tabs.overview">Overview</Trans>
              </TabsTrigger>
              <TabsTrigger value="days">
                <Trans id="settlingIn.tabs.days">Days</Trans>
              </TabsTrigger>
              <TabsTrigger value="progress">
                <Trans id="settlingIn.tabs.progress">Progress</Trans>
              </TabsTrigger>
            </ScrollableTabsList>

            <TabsContent value="overview">
              <OverviewTab plan={plan} planId={planId} />
            </TabsContent>
            <TabsContent value="days">
              <DaysTab
                planId={planId}
                scheduleEntries={plan.schedule_entries || []}
                trackingEntries={plan.tracking_entries || []}
                childId={plan.child_id || ""}
                startDate={plan.start_date}
                completed={plan.settling_in_completed === true}
              />
            </TabsContent>
            <TabsContent value="progress">
              <ProgressTab
                planId={planId}
                progressEntries={plan.progress_entries || []}
                scheduleEntries={plan.schedule_entries || []}
              />
            </TabsContent>
          </Tabs>

          <ClosureChecklistCard
            plan={plan}
            onComplete={() => setShowCompleteDialog(true)}
            onSignOff={() => setShowSignOffDialog(true)}
            onGoToTab={setActiveTab}
            className="lg:w-80 lg:shrink-0"
          />
        </div>
      </PageContainer>

      {/* Complete dialog */}
      <CompleteDialog
        open={showCompleteDialog}
        onOpenChange={setShowCompleteDialog}
        planId={planId}
        endDate={plan.end_date}
        isCompleted={plan.settling_in_completed}
        // Resolves the billing preview's target invoice period when the plan is
        // already signed off; omitted (nil period) when it is not.
        signOffDate={plan.sign_off_date}
      />

      {/* Terminate confirmation. Wired to the UNCHANGED soft-delete endpoint —
          S9 is presentation only. It now states the action's true consequence:
          the contract reminder, which previously appeared only on the OverviewTab
          card that the header Delete bypassed entirely. */}
      <ConfirmDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        variant="destructive"
        title={<Trans id="settlingIn.terminate.title">Terminate settling-in plan?</Trans>}
        description={
          <Trans id="settlingIn.overview.terminateExplanation">
            Terminating the settling-in phase will cancel the settling-in phase completely. Please re-schedule or terminate the contract as well.
          </Trans>
        }
        confirmLabel={<Trans id="settlingIn.actions.terminate">Terminate</Trans>}
        onConfirm={handleDelete}
        isPending={deleteMutation.isPending}
      />

      {/* Sign-off dialog */}
      <SignOffDialog
        open={showSignOffDialog}
        onOpenChange={setShowSignOffDialog}
        planId={planId}
        childId={plan.child_id || ""}
        isSignedOff={isSignedOff}
        signOffDate={plan.sign_off_date}
        signOffContactId={plan.sign_off_contact_id}
      />
    </>
  );
}
